import { useEffect, useState } from 'react';
import { resolveGpc, type GpcDisplay } from '@/lib/gpc-taxonomy';
import { useLanguage } from '@/hooks/useLanguage';

interface GpcDisplayValueProps {
  value: string | null | undefined;
  className?: string;
}

/**
 * Renders a Google Product Category value as both localized text path and numeric ID.
 * Used in product comparison views where the user needs to verify the category.
 *
 * - Stored numeric IDs (e.g. "1604") → resolved to "Bekleidung > Kleidung > Kleider" + "ID: 1604"
 * - Legacy text paths → shown as-is (no ID line)
 * - Empty/null → em-dash placeholder
 */
/**
 * Aufgeloester GPC-Wert als Hook, fuer Darstellungen, die Text und ID selbst
 * anordnen (z.B. die Kategorie-Schlusszeile der Werkbank). GpcDisplayValue
 * nutzt denselben Hook - eine Aufloesungslogik, zwei Layouts.
 */
export function useResolvedGpc(value: string | null | undefined): GpcDisplay {
  const { language } = useLanguage();
  const [resolved, setResolved] = useState<GpcDisplay>({ id: null, text: null });

  useEffect(() => {
    let cancelled = false;
    resolveGpc(value, language).then((r) => {
      if (!cancelled) setResolved(r);
    });
    return () => {
      cancelled = true;
    };
  }, [value, language]);

  return resolved;
}

export function GpcDisplayValue({ value, className }: GpcDisplayValueProps) {
  const resolved = useResolvedGpc(value);

  if (!value) {
    return <span className="text-muted-foreground">—</span>;
  }

  // While loading: show raw value as fallback
  const displayText = resolved.text ?? value;

  return (
    <span className={`inline-flex flex-col gap-0.5 ${className ?? ''}`}>
      <span>{displayText}</span>
      {resolved.id && (
        <span className="text-xs text-foreground/70 font-mono">ID: {resolved.id}</span>
      )}
    </span>
  );
}
